import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { AppBar, Toolbar, Typography } from '@mui/material';
import useApi from '../hooks/useApi';
import { ConfigContext } from './ConfigContext';

/**
 * ConfigProvider component is responsible for fetching and providing configuration data.
 * It attempts to fetch the configuration data from an API and falls back to local storage if necessary.
 * It also displays feedback via AppBar when loading or on fetch errors.
 *
 * @param {Object} props - The component props.
 * @param {React.ReactNode} props.children - The children components that will consume the configuration context.
 * @returns {JSX.Element} The Config Provider component that wraps its children with configuration context.
 */
export const ConfigProvider = (props) => {
	const [loading, setLoading] = useState(true);
	const [config, setConfig] = useState({});
	const [fetchError, setFetchError] = useState(false);
	const localStorageKey = `${process.env.REACT_APP_APPID}_uiConfig`;
	const api = useApi();

	useEffect(() => {
		/**
		 * Asynchronously fetch the configuration from the API.
		 * Falls back to local storage if the fetch fails.
		 */
		const fetchConfig = async () => {
			try {
				const result = await api.config.fetch({});
				localStorage.setItem(localStorageKey, JSON.stringify(result));
				setConfig(result);
				setFetchError(false);
			} catch (error) {
				console.error('Failed to fetch config from API, using local storage if available', error);
				const storedConfig = localStorage.getItem(localStorageKey);
				if (storedConfig) {
					setConfig(JSON.parse(storedConfig));
				} else {
					console.error('No local config found');
					setFetchError(true);
				}
			} finally {
				setLoading(false);
			}
		};

		fetchConfig();
	}, [localStorageKey]);

	return (
		<>
			{fetchError && (
				<AppBar position="static" style={{ height: '25px', backgroundColor: 'red' }}>
					<Toolbar variant="dense" style={{ minHeight: '25px' }}>
						<Typography variant="caption" color="white">
							Unable to fetch configuration data from the API
						</Typography>
					</Toolbar>
				</AppBar>
			)}
			{loading && !fetchError && (
				<AppBar position="static" style={{ height: '25px', backgroundColor: 'green' }}>
					<Toolbar variant="dense" style={{ minHeight: '25px' }}>
						<Typography variant="caption" color="white">
							Loading configuration data...
						</Typography>
					</Toolbar>
				</AppBar>
			)}
			{!loading && !fetchError && (
				<ConfigContext.Provider value={{ config, setConfig }}>
					{props.children}
				</ConfigContext.Provider>
			)}
		</>
	);
};

ConfigProvider.propTypes = {
	children: PropTypes.node.isRequired,
};
